import { type BaseSyntheticEvent, useEffect, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';

export type FormLike = {
    formState: {
        isValid: boolean;
        isDirty: boolean;
        isSubmitting: boolean;
        errors: { root?: { message?: string } };
    };
    reset: () => void;
    trigger: () => void;
};

type SubmitData<T extends object> = {
    [K in keyof T]: T[K] extends UseFormReturn<infer U> | undefined ? U : never;
};

export const useMultiForm = <
    // biome-ignore lint/suspicious/noExplicitAny: does not apply
    F extends Record<string, UseFormReturn<any>>,
>(
    forms: F,
    submitHandler: (data: SubmitData<F>) => Promise<void> | void
) => {
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isDirty = Object.values(forms).some((form) => form.formState.isDirty);

    const isValid = Object.values(forms).every(
        (form) => form.formState.isValid
    );

    const rootError = Object.values(forms)
        .map((form) => form.formState.errors.root?.message)
        .filter(Boolean)
        .join(';');

    const onSubmit = async (e?: BaseSyntheticEvent) => {
        e?.preventDefault();

        if (!isValid) {
            // TODO onInvalid notification
            return;
        }

        setIsSubmitting(true);

        const entries: Array<[string, unknown]> = [];
        let wasInvalid = false;

        // TODO await Promise.all
        for (const [key, form] of Object.entries(forms)) {
            await form.handleSubmit(
                (data) => entries.push([key, data]),
                () => {
                    wasInvalid = true;
                }
            )();
        }
        if (wasInvalid) {
            setIsSubmitting(false);
            // TODO onInvalid notification
            return;
        }

        const data = Object.fromEntries(entries);
        await submitHandler(data as SubmitData<F>);

        setIsSubmitting(false);
    };

    const reset = () =>
        Object.values(forms).forEach((form) => void form.reset());

    const trigger = () =>
        Object.values(forms).forEach((form) => void form.trigger());

    useEffect(() => {
        Object.values(forms).forEach((form) => {
            void (async () => {
                await form.trigger();
                form.clearErrors();
            })();
        });
    }, []);

    return {
        onSubmit,
        form: {
            formState: {
                isSubmitting,
                isDirty,
                isValid,
                errors: { root: { message: rootError } },
            },
            reset,
            trigger,
        } satisfies FormLike,
    };
};
